import { useState } from 'react';
import {
  Box,
  Flex,
  Slider,
  SliderTrack,
  SliderFilledTrack,
} from '@chakra-ui/react';
import { useLocation, useHistory } from 'react-router-dom';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { KText } from '@/ui-library/components/text';
import { Expand03 } from '@kula-ai/icons-core';
import { useBulkOperationsOptional } from '@/components/ats/BulkOperationQueue';
import {
  UI_ACTIVITIES_COMPLETED,
  UI_ACTIVITY_TRACKER,
} from '@/components/ats/BulkOperationQueue/BulkOperationsContext';

interface NavActivityTrackerProps {
  isCollapsed: boolean;
}

export const NavActivityTracker = ({
  isCollapsed,
}: NavActivityTrackerProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const history = useHistory();

  // Get bulk operations state from context (uses skip_viewed=true)
  const bulkOps = useBulkOperationsOptional();

  // Count: active + completed (tracker stays visible until user opens Recent activities)
  const activityCount = bulkOps?.totalOperations || 0;

  // Completed = no active operations but we still have activities to show
  const isCompleted =
    !bulkOps?.hasActiveOperations && (bulkOps?.totalOperations ?? 0) > 0;

  // Hide on Recent Activities page since all activities are shown there
  const isOnRecentActivitiesPage =
    location.pathname === '/notifications/recent-activities';

  // Show when minimized and we have any operations (in-progress or completed).
  // Hide when activities were just marked as viewed (close/minimize) until refetch completes to avoid 1s flash.
  const isVisible =
    bulkOps?.viewMode === 'minimized' &&
    activityCount > 0 &&
    !isOnRecentActivitiesPage &&
    !bulkOps?.activitiesJustMarkedAsViewed;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Don't render if not visible
  if (!isVisible || !bulkOps) {
    return null;
  }

  const { title, progress, isSingleOperation } = bulkOps;

  // When completed: show "Activities completed" and 100%; otherwise use context title/progress
  const displayTitle = isCompleted
    ? UI_ACTIVITIES_COMPLETED
    : isSingleOperation
      ? title
      : UI_ACTIVITY_TRACKER;
  const displayProgress = isCompleted ? 100 : progress;

  // Navigate to Recent activities; mark as viewed so tracker hides after refetch
  const handleExpand = () => {
    bulkOps.markActivitiesAsViewed?.();
    history.push('/notifications/recent-activities');
  };

  // Collapsed sidebar view - full width with badge and progress bar at bottom
  if (isCollapsed) {
    return (
      <Box
        bg="ats-bg-secondary"
        // Extend beyond parent padding to fill full width
        mx="-8px"
        h="3.75rem"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        cursor="pointer"
        onClick={handleExpand}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        transition="all 0.1s"
        overflow="hidden"
        position="relative"
      >
        {/* Badge centered - always visible */}
        <Flex flex={1} alignItems="center" justifyContent="center">
          <KText
            bg="secondary-950"
            color="base-white"
            borderRadius="4px"
            px="8px"
            py="2px"
            size="sm-semi-bold"
            minW="24px"
            textAlign="center"
            border="1px solid"
            borderColor="secondary-700"
          >
            {activityCount}
          </KText>
        </Flex>
        {/* Progress bar - full width at bottom */}
        <Box w="100%" px="8px" mb="8px">
          <Box
            w="100%"
            h="6px"
            bg="whiteAlpha.300"
            borderRadius="6px"
            overflow="hidden"
          >
            <Box
              h="100%"
              w={`${displayProgress}%`}
              bg="linear-gradient(90deg, #8A90FF 0%, #B15FDA 100%)"
              transition="width 0.1s"
              borderTopLeftRadius="6px"
              borderBottomLeftRadius="6px"
            />
          </Box>
        </Box>
        {/* Expand icon overlay on hover */}
        {isHovered && (
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            alignItems="center"
            justifyContent="center"
            bg="ats-bg-secondary"
          >
            <Expand03
              size={20}
              color="var(--kula-colors-base-white)"
              className="kula-icon"
            />
          </Flex>
        )}
      </Box>
    );
  }

  // Expanded sidebar view
  return (
    <Box
      bg="ats-bg-secondary"
      borderRadius="8px"
      overflow="hidden"
      cursor="pointer"
      onClick={handleExpand}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      transition="all 0.1s"
    >
      <Flex h="44px" alignItems="center" px="12px" gap="8px">
        {/* Title - "Activities completed" when done, otherwise activity title or "Activity Tracker" */}
        <Box flex={1} minW={0}>
          <TruncatedTextWithTooltip
            text={displayTitle}
            color="base-white"
            size="sm-semi-bold"
            placement="top"
            toolTipVariant="default"
            tooltipProps={{
              hasArrow: false,
            }}
          />
        </Box>

        {/* Count badge */}
        {activityCount > 0 && (
          <KText
            bg="secondary-950"
            color="base-white"
            borderRadius="4px"
            px="8px"
            py="2px"
            size="sm-semi-bold"
            flexShrink={0}
            border="1px solid"
            borderColor="secondary-700"
          >
            {activityCount}
          </KText>
        )}

        {/* Expand icon - always visible */}
        <Expand03
          size={16}
          color="var(--kula-colors-base-white)"
          className="kula-icon"
          style={{ flexShrink: 0 }}
        />
      </Flex>

      {/* Progress bar at bottom */}
      <Box px="12px" pb="8px">
        <Slider
          value={displayProgress}
          min={0}
          max={100}
          step={1}
          colorScheme="primary"
          size="sm"
          isReadOnly
          w="100%"
        >
          <SliderTrack bg="whiteAlpha.300" h="4px" borderRadius="6px">
            <SliderFilledTrack
              bg="linear-gradient(90deg, #8A90FF 0%, #B15FDA 100%)"
              borderRadius="6px"
            />
          </SliderTrack>
        </Slider>
      </Box>
    </Box>
  );
};

export default NavActivityTracker;
